import React from "react";
import { motion } from "framer-motion";
import { FaGuitar, FaComments, FaUsers, FaStar } from "react-icons/fa";
import studioImage from "../images/gallery/mainImage.webp";

const usps = [
  {
    icon: FaGuitar,
    title: "In-Studio Practice",
    description:
      "Real instruments in a professional studio with proper acoustics and soundproofing.",
  },
  {
    icon: FaComments,
    title: "Interactive Sessions",
    description:
      "Live Q&A, practice corrections, and one-on-one guidance keep learning engaging.",
  },
  {
    icon: FaUsers,
    title: "Group Learning",
    description:
      "Small groups of like-minded learners exchange tips and play together.",
  },
  {
    icon: FaStar,
    title: "Performance Opportunities",
    description:
      "Student showcases and live performances that build real stage confidence.",
  },
];

const listVariant = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.12 } },
};

const rowVariant = {
  hidden: { opacity: 0, x: 24 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.5 } },
};

const AboutUs = () => {
  return (
    <section className="bg-white px-6 py-24 sm:px-12 sm:py-28 lg:px-20">
      <div className="mx-auto grid max-w-6xl grid-cols-1 items-center gap-14 lg:grid-cols-2">
        {/* Image */}
        <motion.div
          className="relative"
          initial={{ opacity: 0, x: -30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
        >
          <img
            src={studioImage}
            alt="Inside the ABC School of Music studio"
            className="aspect-[4/3] w-full rounded-3xl object-cover shadow-card"
          />
          {/* floating stat card */}
          <motion.div
            className="absolute -bottom-6 -right-4 rounded-2xl bg-white px-6 py-4 shadow-card ring-1 ring-gray-100 sm:-right-6"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <p className="font-display text-3xl font-bold text-brand">1000+</p>
            <p className="text-xs font-medium uppercase tracking-wide text-ink-soft">
              Students Trained
            </p>
          </motion.div>
        </motion.div>

        {/* Content */}
        <div>
          <motion.span
            className="eyebrow mb-5"
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            Why Learn With Us
          </motion.span>
          <motion.h2
            className="section-heading"
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            About Our Offline Classes
          </motion.h2>
          <motion.p
            className="mt-5 max-w-xl text-base leading-relaxed text-ink-soft sm:text-lg"
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            Immersive, hands-on learning that goes far beyond screen-based
            lessons — real-time feedback, jam sessions, and personalized
            mentorship.
          </motion.p>

          <motion.div
            className="mt-10 grid grid-cols-1 gap-x-8 gap-y-7 sm:grid-cols-2"
            variants={listVariant}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
          >
            {usps.map((usp) => {
              const Icon = usp.icon;
              return (
                <motion.div
                  key={usp.title}
                  className="group flex gap-4"
                  variants={rowVariant}
                >
                  <div className="flex h-11 w-11 flex-none items-center justify-center rounded-xl bg-gray-100 text-ink transition-colors duration-300 group-hover:bg-brand group-hover:text-white">
                    <Icon />
                  </div>
                  <div>
                    <h3 className="font-display text-base font-semibold text-ink">
                      {usp.title}
                    </h3>
                    <p className="mt-1 text-sm text-ink-soft">
                      {usp.description}
                    </p>
                  </div>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default AboutUs;
